import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, output, viewChild } from '@angular/core';
import { ActivityEntityName, EntityQueue, EntityQueueType, MetadataEntityData } from '@jms/model';
import { SettingsPanelComponent, createSettingsPanelWizard } from '@workbuddy/ui/v2/blocks/settings.panel';
import { SideDialogComponent } from '@workbuddy/ui/v2/blocks/side.dialog';
import { ViewSettingsValue } from '@workbuddy/ui/v2/blocks/entity.board/components/view.settings';
import { QueueAdapterContext, QueueAdapterDeps } from './adapters/queue.step.adapter';
import { createSetupAdapter } from './adapters/setup.step.adapter';
import { createFlagJobAdapter } from './adapters/flag.job.step.adapter';
import { createFlagStageAdapter } from './adapters/flag.stage.step.adapter';
import { createEntityJobAdapter } from './adapters/entity.job.step.adapter';
import { createEntityStageAdapter } from './adapters/entity.stage.step.adapter';
import { createEntityBillAdapter, createEntityInvoiceAdapter } from './adapters/entity.finance.step.adapter';
import { ENTITY_BILL_STEP_ID, ENTITY_INVOICE_STEP_ID, ENTITY_JOB_STEP_ID, ENTITY_STAGE_STEP_ID, FLAG_JOB_STEP_ID, FLAG_STAGE_STEP_ID, SETUP_STEP_ID, resolveStep2Id } from './queue.step.routing';

export interface QueueSettingsConfig {
  queue: Partial<EntityQueue> | null;
  metadataMap: Record<string, MetadataEntityData>;
  mode?: QueueEditorMode;
  isOwner?: boolean;
}

export type QueueEditorMode = 'create' | 'edit';

export interface QueueSettingsValue {
  queue: Partial<EntityQueue>;
}

function modelFromConfig(config: QueueSettingsConfig | null): Partial<EntityQueue> {

  const queue = config?.queue;
  if (!queue) return {};
  const isEdit = (config?.mode ?? (queue._id ? 'edit' : 'create')) === 'edit';
  // In create mode the draft queue carries a synthetic `_id` — strip it
  // from the model so `initialStep` can use `_id` as the edit-vs-create
  // discriminator without consulting external state.
  if (!isEdit) {
    const { _id, ...rest } = queue;
    return rest;
  }
  return { ...queue };
}

@Component({
  selector: 'queue-settings',
  template: `
    @if ($embedded()) {
      @if (panel(); as p) {
        <wb-settings-panel [panel]="p" [dismissOnClickOutside]="false" [confirmDiscardChanges]="true" (closed)="closed.emit()" />
      }
    } @else {
      <wb-side-dialog [open]="$isOpen()" [mode]="$mode()" [backdrop]="true" (closed)="closed.emit()">
        @if (panel(); as p) {
          <wb-settings-panel [panel]="p" [dismissOnClickOutside]="false" [confirmDiscardChanges]="true" (closed)="onClose()" />
        }
      </wb-side-dialog>
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SettingsPanelComponent, SideDialogComponent],
  host: { class: 'wb-board-settings-panel' },
})
export class QueueSettingsComponent {

  private readonly sideDialog = viewChild(SideDialogComponent);
  private readonly settingsPanel = viewChild(SettingsPanelComponent);

  public $config = input.required<QueueSettingsConfig>({ alias: 'data' });
  public $isOpen = input<boolean>(false, { alias: 'isOpen' });
  public $mode = input<'overlay' | 'push'>('overlay', { alias: 'mode' });
  // A host that already provides dialog chrome (the Queues settings board) mounts the panel bare.
  public $embedded = input<boolean>(false, { alias: 'embedded' });

  public previewViewChange = output<ViewSettingsValue>();
  public previewRequestChange = output<ViewSettingsValue>();
  public valueChange = output<ViewSettingsValue>();
  public saved = output<QueueSettingsValue>();
  public saveAsNew = output<QueueSettingsValue>();
  public reset = output<void>();
  public cancelled = output<void>();
  public deleted = output<void>();
  public closed = output<void>();

  private readonly $model = computed(() => modelFromConfig(this.$config()));

  // Constructor is itself an injection context — adapter factories that
  // call `inject()` / `effect()` / `computed()` at factory body run fine
  // here. The wizard wraps each step's `build()` in its own injection
  // context so any inject() calls inside `build()` also resolve.
  private readonly adapterDeps: QueueAdapterDeps = {
    $config: this.$config,
  };

  private readonly wizard = createSettingsPanelWizard<Partial<EntityQueue>, QueueAdapterContext>({
    model: this.$model,
    // Model-only: presence of `_id` means edit mode (create-mode models
    // strip the draft's synthetic `_id` — see modelFromConfig).
    initialStep: model => model._id ? resolveStep2Id(model.type, model.entity ?? model.targetEntity) : SETUP_STEP_ID,
    context: () => ({
      onPreviewView: value => { this.previewViewChange.emit(value); this.valueChange.emit(value); },
      onPreviewRequest: value => this.previewRequestChange.emit(value),
      onDelete: () => this.deleted.emit(),
      onCancel: () => this.requestClose(),
    }),
    steps: [
      {
        id: SETUP_STEP_ID,
        skipHistory: true,
        build: createSetupAdapter().build,
        commit: values => {

          const type = values['type'] as EntityQueueType;
          const entity = values['entity'] as ActivityEntityName;
          const patch: Partial<EntityQueue> = { name: values['name'] as string, type };
          // Step 1's "entity" lands on different model fields depending on
          // queue type — Flag queues target an entity, Entity queues are
          // of an entity. Terminal-step adapters read whichever field applies.
          if (type === EntityQueueType.Flag) patch.targetEntity = entity;
          else patch.entity = entity;
          return patch;
        },
        nextStep: model => resolveStep2Id(model.type, model.entity ?? model.targetEntity),
      },
      ...([
        [FLAG_JOB_STEP_ID, createFlagJobAdapter(this.adapterDeps)],
        [FLAG_STAGE_STEP_ID, createFlagStageAdapter(this.adapterDeps)],
        [ENTITY_JOB_STEP_ID, createEntityJobAdapter(this.adapterDeps)],
        [ENTITY_STAGE_STEP_ID, createEntityStageAdapter(this.adapterDeps)],
        [ENTITY_INVOICE_STEP_ID, createEntityInvoiceAdapter(this.adapterDeps)],
        [ENTITY_BILL_STEP_ID, createEntityBillAdapter(this.adapterDeps)],
      ] as const).map(([id, adapter]) => ({
        id,
        terminal: true,
        build: adapter.build,
        commit: (values: Record<string, unknown>) => adapter.buildValue!(values, this.wizard.model()),
      })),
    ],
    onDone: model => {

      this.saved.emit({ queue: model });
      // Save succeeded — drop the dirty flag so the post-save navigation
      // (create mode routes to the new queue, firing canDeactivate ->
      // requestClose) doesn't see unsaved changes and prompt to discard.
      this.wizard.panel()?.markPristine();
    },
  });

  protected readonly panel = computed(() => this.wizard.panel());

  protected onClose(): void {

    this.sideDialog()?.close();
  }

  public async requestClose(): Promise<boolean> {

    return await this.settingsPanel()?.requestClose() ?? true;
  }
}
